import {
  Component,
  effect,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { GlobalsStore } from '../../../stores/globals.state';
import { DataLayer } from '../../../models/data-layer';
import { DropdownModule } from 'primeng/dropdown';
import { NgIf } from '@angular/common';
import { SharedModule } from 'primeng/api';
import { getState } from '@ngrx/signals';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { Button } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { DialogModule } from 'primeng/dialog';
import { StyleClassModule } from 'primeng/styleclass';

@Component({
  selector: 'app-layer-dropdown',
  standalone: true,
  imports: [
    DropdownModule,
    NgIf,
    SharedModule,
    FormsModule,
    Button,
    DividerModule,
    OverlayPanelModule,
    DialogModule,
    StyleClassModule,
  ],
  templateUrl: './layer-dropdown.component.html',
  styleUrl: './layer-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LayerDropdownComponent),
      multi: true,
    },
  ],
})
export class LayerDropdownComponent implements OnChanges, ControlValueAccessor {
  readonly layerStore = inject(DataLayerStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly globalStore = inject(GlobalsStore);

  @Input() label = 'Jahr';
  @Input() name = 'year';
  @Input() displayNewYearOption = false;
  @Input() selection: DataLayer | null = null;
  @Input() globalSelector = false;
  @Input() disabled = false;
  @Input() enableEmpty = false;
  @Input() withData = false;
  @Input() withDataInIndicators?: string[];
  @Input() selectedTypeId: string | null = null;
  @Input() isStandalone = false;
  @Output() selectionChange = new EventEmitter<DataLayer>();

  addNewLayerDialog = false;
  touched = false;

  constructor() {
    effect(() => {
      const globalState = getState(this.globalStore);
      const layerState = getState(this.layerStore);
      if (!this.enableEmpty && this.globalSelector) {
        this.selectedTypeId = globalState.selectedType?._id || null;
        this.selection = globalState.selectedLayer;
      }

      if (layerState && layerState.selectedId && this.displayNewYearOption) {
        this.selection = this.layerStore.getById(layerState.selectedId);
        this.onChange(this.selection);
        this.closeLayerForm();
      }
    });
  }

  /**
   * Handles changes to the component's input properties.
   * This method is triggered whenever any data-bound input properties change,
   * allowing updates to be made based on the new values.
   *
   * @param {SimpleChanges} changes - An object that contains the current and previous values of the changed inputs.
   * The `SimpleChanges` parameter provides information about the properties that have been modified, including their previous and current values.
   *
   * @return {void} This method does not return a value.
   */
  ngOnChanges(changes: SimpleChanges) {
    if (
      !this.globalSelector &&
      changes['selectedTypeId'] &&
      changes['selectedTypeId'].currentValue === ''
    ) {
      this.selection = null;
    } else if (
      changes['selectedTypeId'] &&
      changes['selectedTypeId'].currentValue !==
        changes['selectedTypeId'].previousValue
    ) {
      this.selection = this.selection || this.typeLayers[0];
      this.onChange(this.selection);
    }
  }

  /**
   * Retrieves a filtered list of data layers based on the selected criteria, such as selected type ID, data availability in indicators, or general data availability.
   *
   * @return {DataLayer[]} A list of data layers that match the filtering conditions. If no conditions are specified, all layers of the selected type are returned.
   */
  get typeLayers(): DataLayer[] {
    const layers = this.selectedTypeId
      ? this.layerStore.groupedByType()[this.selectedTypeId]
      : [];

    let filtered: DataLayer[] = [];
    if (this.withDataInIndicators && this.withDataInIndicators.length) {
      const indicatorIds = this.withDataInIndicators;
      filtered = layers.filter(layer => {
        return this.dataStore.dataLayerAndIndicatorsHasData(
          layer._id,
          indicatorIds
        );
      });
    } else if (this.withData) {
      filtered = layers.filter(layer =>
        this.dataStore.dataLayerHasData(layer._id)
      );
    } else {
      filtered = layers;
    }

    return filtered;
  }

  /**
   * Handles the change event in the data layer selection process.
   *
   * @param {DataLayer} value - The selected data layer object.
   *
   * This function emits the updated selection via the `selectionChange`
   * event after a delay of 300ms. If `globalSelector` is enabled and
   * a valid `value` is provided, it also updates the global store
   * with the selected layer.
   */
  protected onChange = (value: DataLayer) => {
    setTimeout(() => {
      this.selectionChange.emit(value);
      if (this.globalSelector && value)
        this.globalStore.setSelectedLayer(value);
    }, 300);
  };

  private onTouched = () => {
    this.touched = false;
  };

  writeValue(value: any): void {
    this.selection = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * Opens a form for adding a new layer. Sets the state to display the add layer dialog and triggers an event to notify listeners.
   *
   * @return {void} This method does not return a value.
   */
  openLayerForm() {
    this.addNewLayerDialog = true;
    this.globalStore.triggerAtlasEvent('addLayerDialog', {
      dataLayerTypeId: this.selectedTypeId || undefined,
    });
  }

  closeLayerForm() {
    this.addNewLayerDialog = false;
  }
}
